import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Trash2, Search } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { TemplatePreview } from './TemplatePreview';
import { useCreateTemplate, useUpdateTemplate } from '@/hooks/useTemplates';
import type { Template, TemplateType } from '@/types/api.types';

// ── Schema ───────────────────────────────────────────────────────────────────

const schema = z
  .object({
    name: z.string().min(1, 'Nome obrigatório'),
    type: z.enum(['text', 'template', 'file', 'contact', 'location']),
    isActive: z.boolean(),
    // text
    text: z.string(),
    // template
    zenviaTemplateId: z.string(),
    variables: z.array(z.object({ name: z.string().min(1), defaultValue: z.string() })),
    // file
    fileUrl: z.string(),
    fileMimeType: z.string(),
    fileCaption: z.string(),
    // contact
    contacts: z.array(
      z.object({ formattedName: z.string(), phones: z.string(), emails: z.string() }),
    ),
    // location
    latitude: z.string(),
    longitude: z.string(),
    locationName: z.string(),
    locationAddress: z.string(),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'text' && !val.text.trim()) {
      ctx.addIssue({ code: 'custom', path: ['text'], message: 'Conteúdo obrigatório' });
    }
    if (val.type === 'template' && !val.zenviaTemplateId.trim()) {
      ctx.addIssue({
        code: 'custom',
        path: ['zenviaTemplateId'],
        message: 'ID do template Zenvia obrigatório',
      });
    }
    if (val.type === 'file' && !val.fileUrl.trim()) {
      ctx.addIssue({ code: 'custom', path: ['fileUrl'], message: 'URL do arquivo obrigatória' });
    }
    if (val.type === 'contact') {
      if (!val.contacts.length) {
        ctx.addIssue({
          code: 'custom',
          path: ['contacts'],
          message: 'Adicione ao menos um contato',
        });
      }
      val.contacts.forEach((c, i) => {
        if (!c.formattedName.trim())
          ctx.addIssue({
            code: 'custom',
            path: ['contacts', i, 'formattedName'],
            message: 'Nome obrigatório',
          });
        if (!c.phones.trim())
          ctx.addIssue({
            code: 'custom',
            path: ['contacts', i, 'phones'],
            message: 'Telefone obrigatório',
          });
      });
    }
    if (val.type === 'location') {
      if (!val.latitude.trim())
        ctx.addIssue({ code: 'custom', path: ['latitude'], message: 'Latitude obrigatória' });
      else if (isNaN(parseFloat(val.latitude)))
        ctx.addIssue({ code: 'custom', path: ['latitude'], message: 'Valor numérico inválido' });
      if (!val.longitude.trim())
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Longitude obrigatória' });
      else if (isNaN(parseFloat(val.longitude)))
        ctx.addIssue({ code: 'custom', path: ['longitude'], message: 'Valor numérico inválido' });
    }
  });

type Values = z.infer<typeof schema>;

// ── Content helpers ───────────────────────────────────────────────────────────

function parseContent(
  type: TemplateType,
  content: unknown,
  zenviaTemplateId: string | null,
  entityVariables?: unknown,
): Partial<Values> {
  const c = (content ?? {}) as Record<string, unknown>;
  switch (type) {
    case 'text':
      return { text: (c['text'] as string) ?? '' };
    case 'template': {
      // content.fields holds user-defined default values; for synced templates it may be {}
      const fields = (c['fields'] as Record<string, string> | null) ?? {};
      const fromFields = Object.entries(fields).map(([name, defaultValue]) => ({
        name,
        defaultValue,
      }));
      // Fall back to entity variables (string[] of field names) for synced templates
      const fromEntity = Array.isArray(entityVariables)
        ? (entityVariables as string[]).map((name) => ({ name, defaultValue: '' }))
        : [];
      return {
        zenviaTemplateId: zenviaTemplateId ?? (c['templateId'] as string) ?? '',
        variables: fromFields.length > 0 ? fromFields : fromEntity,
      };
    }
    case 'file':
      return {
        fileUrl: (c['fileUrl'] as string) ?? '',
        fileMimeType: (c['fileMimeType'] as string) ?? '',
        fileCaption: (c['fileCaption'] as string) ?? '',
      };
    case 'contact': {
      const cts =
        (c['contacts'] as Array<{
          formattedName: string;
          phones: string[];
          emails?: string[];
        }> | null) ?? [];
      return {
        contacts: cts.map((ct) => ({
          formattedName: ct.formattedName,
          phones: ct.phones.join(', '),
          emails: (ct.emails ?? []).join(', '),
        })),
      };
    }
    case 'location':
      return {
        latitude: String((c['latitude'] as string | number | undefined) ?? ''),
        longitude: String((c['longitude'] as string | number | undefined) ?? ''),
        locationName: (c['name'] as string) ?? '',
        locationAddress: (c['address'] as string) ?? '',
      };
  }
}

function buildContent(values: Values): unknown {
  switch (values.type) {
    case 'text':
      return { type: 'text', text: values.text };
    case 'template':
      return {
        type: 'template',
        templateId: values.zenviaTemplateId,
        fields: Object.fromEntries(
          values.variables.map(({ name, defaultValue }) => [name, defaultValue]),
        ),
      };
    case 'file':
      return {
        type: 'file',
        fileUrl: values.fileUrl,
        ...(values.fileMimeType && { fileMimeType: values.fileMimeType }),
        ...(values.fileCaption && { fileCaption: values.fileCaption }),
      };
    case 'contact':
      return {
        type: 'contact',
        contacts: values.contacts.map((c) => ({
          name: { formattedName: c.formattedName },
          phones: c.phones
            .split(',')
            .map((p) => ({ phone: p.trim() }))
            .filter((p) => p.phone),
          emails: c.emails
            ? c.emails
                .split(',')
                .map((e) => ({ email: e.trim() }))
                .filter((e) => e.email)
            : [],
        })),
      };
    case 'location':
      return {
        type: 'location',
        latitude: parseFloat(values.latitude),
        longitude: parseFloat(values.longitude),
        ...(values.locationName && { name: values.locationName }),
        ...(values.locationAddress && { address: values.locationAddress }),
      };
  }
}

function getDefaultValues(template?: Template | null): Values {
  const base: Values = {
    name: template?.name ?? '',
    type: template?.type ?? 'text',
    isActive: template?.isActive ?? true,
    text: '',
    zenviaTemplateId: template?.zenviaTemplateId ?? '',
    variables: [],
    fileUrl: '',
    fileMimeType: '',
    fileCaption: '',
    contacts: [{ formattedName: '', phones: '', emails: '' }],
    latitude: '',
    longitude: '',
    locationName: '',
    locationAddress: '',
  };
  if (template) {
    return {
      ...base,
      ...parseContent(
        template.type,
        template.content,
        template.zenviaTemplateId,
        template.variables,
      ),
    };
  }
  return base;
}

// ── MIME type options ─────────────────────────────────────────────────────────

const MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'video/mp4',
  'audio/ogg',
  'audio/mpeg',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

// ── Component ────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: Template | null;
}

export function TemplateFormDialog({ open, onOpenChange, template }: Props) {
  const isEdit = !!template;
  const { mutate: create, isPending: creating } = useCreateTemplate();
  const { mutate: update, isPending: updating } = useUpdateTemplate();
  const isPending = creating || updating;

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: getDefaultValues(template),
  });

  const {
    fields: varFields,
    append: appendVar,
    remove: removeVar,
  } = useFieldArray({ control: form.control, name: 'variables' });

  const {
    fields: contactFields,
    append: appendContact,
    remove: removeContact,
  } = useFieldArray({ control: form.control, name: 'contacts' });

  const watchedType = form.watch('type');
  const watchedValues = form.watch();

  useEffect(() => {
    if (open) form.reset(getDefaultValues(template));
  }, [open, template]); // eslint-disable-line react-hooks/exhaustive-deps

  function detectVariables() {
    const text = form.getValues('text');
    const matches = [
      ...new Set(
        [...text.matchAll(/\{\{(\w+)\}\}/g)].map((m) => m[1]).filter((n): n is string => !!n),
      ),
    ];
    const existing = form.getValues('variables').map((v) => v.name);
    const toAdd = matches.filter((name) => !existing.includes(name));
    toAdd.forEach((name) => appendVar({ name, defaultValue: '' }));
  }

  function onSubmit(values: Values) {
    const payload = {
      name: values.name,
      type: values.type,
      content: buildContent(values),
      ...(values.type === 'template'
        ? { zenviaTemplateId: values.zenviaTemplateId || undefined }
        : {}),
    };

    if (isEdit && template) {
      update(
        { id: template.id, data: { ...payload, isActive: values.isActive } },
        { onSuccess: () => onOpenChange(false) },
      );
    } else {
      create(payload, { onSuccess: () => onOpenChange(false) });
    }
  }

  const showPreview = watchedType === 'text' || watchedType === 'template';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={showPreview ? 'sm:max-w-4xl' : 'sm:max-w-lg'}>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar template' : 'Novo template'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={(e) => void form.handleSubmit(onSubmit)(e)}>
            <div className={`gap-6 ${showPreview ? 'grid grid-cols-2' : 'flex flex-col'}`}>
              {/* ── Left: form fields ── */}
              <div className="space-y-4 overflow-y-auto" style={{ maxHeight: '70vh' }}>
                {/* Name */}
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Nome</FormLabel>
                      <FormControl>
                        <Input placeholder="Boas-vindas" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Type selector */}
                <FormField
                  control={form.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo</FormLabel>
                      <div className="flex flex-wrap gap-2">
                        {(['text', 'template', 'file', 'contact', 'location'] as const).map((t) => (
                          <label
                            key={t}
                            className={`flex cursor-pointer items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors ${
                              field.value === t
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-input hover:bg-accent'
                            }`}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              value={t}
                              checked={field.value === t}
                              onChange={() => field.onChange(t)}
                            />
                            {
                              {
                                text: 'Texto',
                                template: 'Template',
                                file: 'Arquivo',
                                contact: 'Contato',
                                location: 'Localização',
                              }[t]
                            }
                          </label>
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* TEXT section */}
                {watchedType === 'text' && (
                  <>
                    <FormField
                      control={form.control}
                      name="text"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Conteúdo</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Olá, {{nome}}! Bem-vindo(a)."
                              rows={5}
                              {...field}
                            />
                          </FormControl>
                          <FormDescription>
                            Use {`{{nome}}`} para inserir variáveis dinâmicas.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={detectVariables}>
                      <Search className="h-3 w-3" />
                      Detectar variáveis
                    </Button>
                    <VariablesSection fields={varFields} onRemove={removeVar} form={form} />
                  </>
                )}

                {/* TEMPLATE section */}
                {watchedType === 'template' && (
                  <>
                    <FormField
                      control={form.control}
                      name="zenviaTemplateId"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>ID do template Zenvia</FormLabel>
                          <FormControl>
                            <Input placeholder="template_id_zenvia" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">Variáveis</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => appendVar({ name: '', defaultValue: '' })}
                        >
                          <Plus className="h-3 w-3" />
                          Adicionar
                        </Button>
                      </div>
                      <VariablesSection fields={varFields} onRemove={removeVar} form={form} />
                    </div>
                  </>
                )}

                {/* FILE section */}
                {watchedType === 'file' && (
                  <>
                    <FormField
                      control={form.control}
                      name="fileUrl"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL do arquivo</FormLabel>
                          <FormControl>
                            <Input placeholder="https://..." {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="fileMimeType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tipo MIME (opcional)</FormLabel>
                          <Select value={field.value} onValueChange={field.onChange}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Selecionar tipo" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="">Não especificar</SelectItem>
                              {MIME_TYPES.map((m) => (
                                <SelectItem key={m} value={m}>
                                  {m}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="fileCaption"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Legenda (opcional)</FormLabel>
                          <FormControl>
                            <Textarea placeholder="Descrição do arquivo…" rows={3} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {/* CONTACT section */}
                {watchedType === 'contact' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium">Contatos</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => appendContact({ formattedName: '', phones: '', emails: '' })}
                      >
                        <Plus className="h-3 w-3" />
                        Adicionar
                      </Button>
                    </div>
                    {contactFields.map((f, i) => (
                      <div key={f.id} className="space-y-2 rounded-md border p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">
                            Contato {i + 1}
                          </span>
                          {contactFields.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive"
                              onClick={() => removeContact(i)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                        <FormField
                          control={form.control}
                          name={`contacts.${i}.formattedName`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Nome</FormLabel>
                              <FormControl>
                                <Input placeholder="João Silva" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`contacts.${i}.phones`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">Telefones (vírgula)</FormLabel>
                              <FormControl>
                                <Input placeholder="+5511999999999, +5511888888888" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={form.control}
                          name={`contacts.${i}.emails`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel className="text-xs">E-mails (opcional, vírgula)</FormLabel>
                              <FormControl>
                                <Input placeholder="joao@email.com" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* LOCATION section */}
                {watchedType === 'location' && (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={form.control}
                        name="latitude"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Latitude</FormLabel>
                            <FormControl>
                              <Input placeholder="-23.5505" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="longitude"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Longitude</FormLabel>
                            <FormControl>
                              <Input placeholder="-46.6333" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <FormField
                      control={form.control}
                      name="locationName"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Nome do local (opcional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Escritório central" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="locationAddress"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Endereço (opcional)</FormLabel>
                          <FormControl>
                            <Input placeholder="Av. Paulista, 1000 — São Paulo, SP" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}

                {/* isActive (edit only) */}
                {isEdit && (
                  <FormField
                    control={form.control}
                    name="isActive"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center gap-3">
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              id="tpl-isActive"
                            />
                          </FormControl>
                          <Label htmlFor="tpl-isActive">Ativo</Label>
                        </div>
                      </FormItem>
                    )}
                  />
                )}
              </div>

              {/* ── Right: preview ── */}
              {showPreview && (
                <div className="overflow-y-auto" style={{ maxHeight: '70vh' }}>
                  <TemplatePreview
                    type={watchedType}
                    text={watchedValues.text}
                    variables={watchedValues.variables}
                    zenviaTemplateId={watchedValues.zenviaTemplateId}
                  />
                </div>
              )}
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

// ── Variables sub-section ────────────────────────────────────────────────────

interface VariablesSectionProps {
  fields: { id: string }[];
  onRemove: (index: number) => void;
  form: ReturnType<typeof useForm<Values>>;
}

function VariablesSection({ fields, onRemove, form }: VariablesSectionProps) {
  if (fields.length === 0) return null;

  return (
    <div className="space-y-2">
      {fields.map((f, i) => (
        <div key={f.id} className="flex items-start gap-2">
          <FormField
            control={form.control}
            name={`variables.${i}.name`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input placeholder="nome" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name={`variables.${i}.defaultValue`}
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <Input placeholder="valor padrão" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="text-destructive"
            onClick={() => onRemove(i)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      ))}
    </div>
  );
}
